import { useCallback, useEffect, useState } from 'react'
import { FiArrowRight, FiDownload, FiPhone } from 'react-icons/fi'
import { downloadBevVcard, BEV_CONTACT } from './bevContact'

const COACH_MARK_KEY = 'myscribbl_onboarding_coach_mark_dismissed'

type ArrowDirection = 'left' | 'bottom'

type Step = {
  selector: string
  text: string
  arrow: ArrowDirection
}

const STEPS: Step[] = [
  {
    selector: '.hero-card-adaptive.post-meeting',
    text: 'Bev captured everything from your call \u2014 summary, key facts, and next steps.',
    arrow: 'bottom',
  },
  {
    selector: '.home-section .eyebrow',
    text: 'Here\u2019s the full breakdown of what Bev pulled from your conversation.',
    arrow: 'bottom',
  },
  {
    selector: 'a.nav-item[href="/call-notes"]',
    text: 'All of your calls with Bev will show up here.',
    arrow: 'left',
  },
  {
    selector: 'a.nav-item[href="/action-items"]',
    text: 'Action items from every call get organized here.',
    arrow: 'left',
  },
  {
    selector: 'a.nav-item[href="/contacts"]',
    text: 'Contacts mentioned in your calls appear here.',
    arrow: 'left',
  },
]

type Position = { top: number; left: number }

function getPosition(el: HTMLElement, arrow: ArrowDirection): Position {
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  const rect = el.getBoundingClientRect()
  if (arrow === 'left') {
    return { top: rect.top + rect.height / 2, left: rect.right + 12 }
  }
  return { top: rect.bottom + 12, left: rect.left + Math.min(rect.width, 400) / 2 }
}

function OnboardingCoachMark() {
  const [stepIndex, setStepIndex] = useState(0)
  const [position, setPosition] = useState<Position | null>(null)
  const [showSaveContact, setShowSaveContact] = useState(false)
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(COACH_MARK_KEY) === 'true'
  )

  const positionToStep = useCallback((index: number) => {
    if (index >= STEPS.length) return null
    const step = STEPS[index]
    const el = document.querySelector(step.selector) as HTMLElement | null
    if (!el) return null
    return getPosition(el, step.arrow)
  }, [])

  useEffect(() => {
    if (dismissed) return
    let attempts = 0
    const tryPosition = () => {
      const pos = positionToStep(0)
      if (pos) {
        setPosition(pos)
        return
      }
      attempts++
      if (attempts < 10) {
        timer = window.setTimeout(tryPosition, 500)
      }
    }
    let timer = window.setTimeout(tryPosition, 1200)
    return () => clearTimeout(timer)
  }, [dismissed, positionToStep])

  const handleNext = () => {
    const next = stepIndex + 1
    if (next >= STEPS.length) {
      setPosition(null)
      setShowSaveContact(true)
      return
    }
    setStepIndex(next)
    setPosition(null)
    const el = document.querySelector(STEPS[next].selector) as HTMLElement | null
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setTimeout(() => {
        const rect = el.getBoundingClientRect()
        const step = STEPS[next]
        if (step.arrow === 'left') {
          setPosition({ top: rect.top + rect.height / 2, left: rect.right + 12 })
        } else {
          setPosition({ top: rect.bottom + 12, left: rect.left + Math.min(rect.width, 400) / 2 })
        }
      }, 400)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem(COACH_MARK_KEY, 'true')
    setDismissed(true)
    setPosition(null)
    setShowSaveContact(false)
  }

  const handleSaveContact = () => {
    downloadBevVcard()
    handleDismiss()
  }

  if (dismissed) return null

  if (showSaveContact) {
    return (
      <>
        <div className="onboarding-overlay" onClick={handleDismiss} />
        <div className="onboarding-save-contact-card">
          <div className="onboarding-save-contact-icon">
            <FiPhone size={24} />
          </div>
          <h3>You can also call Bev by phone</h3>
          <p>
            Save Bev&apos;s number <strong>{BEV_CONTACT.phoneNumber}</strong> so
            she&apos;s always one tap away after a visit.
          </p>
          <div className="onboarding-save-contact-actions">
            <button type="button" className="onboarding-btn primary" onClick={handleSaveContact}>
              <FiDownload size={16} />
              Save Contact
            </button>
            <button type="button" className="onboarding-save-contact-skip" onClick={handleDismiss}>
              Maybe later
            </button>
          </div>
        </div>
      </>
    )
  }

  if (!position) return null

  const step = STEPS[stepIndex]
  const isLast = stepIndex === STEPS.length - 1
  const arrowClass = `onboarding-coach-arrow onboarding-coach-arrow-${step.arrow}`

  return (
    <>
      <div className="onboarding-overlay" onClick={handleDismiss} />
      <div
        className={`onboarding-coach-mark onboarding-coach-mark-${step.arrow}`}
        style={{ top: position.top, left: position.left }}
        key={stepIndex}
      >
        <div className={arrowClass} />
        <div className="onboarding-coach-content">
          <p>{step.text}</p>
          <div className="onboarding-coach-footer">
            <span className="onboarding-coach-step-count">
              {stepIndex + 1} of {STEPS.length}
            </span>
            <button type="button" className="onboarding-coach-next" onClick={handleNext}>
              {isLast ? 'Got it' : 'Next'}
              <FiArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default OnboardingCoachMark
